import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DATE_PRESET_OPTIONS, type DatePreset } from '../../utils/dateRangeFilter';

interface DateRangeFilterProps {
  /** Current preset selection ('' = Any date, 'custom' reveals the From/To pickers). */
  preset: DatePreset;
  /** Custom-range bounds — only meaningful while preset === 'custom'. */
  from: Date | null;
  to: Date | null;
  /** Emits the full next state (preset + custom bounds) in one call. */
  onChange: (next: { preset: DatePreset; from: Date | null; to: Date | null }) => void;
  disabled?: boolean;
  label?: string;
  /** Unique id fragment for the label/select pairing (avoid duplicate ids if two render together). */
  idPrefix?: string;
}

/**
 * Created-date list filter: a preset dropdown (Any / Last 7·14·30·90 days / Custom range) plus, when
 * "Custom range" is chosen, From/To date pickers. Self-contained (own LocalizationProvider) so it drops
 * into any toolbar. The effective date bounds are computed at request time via computeDateRange — for a
 * preset the pickers are hidden and the custom bounds are cleared.
 */
export const DateRangeFilter = ({
  preset,
  from,
  to,
  onChange,
  disabled = false,
  label = 'Date',
  idPrefix = 'date-preset',
}: DateRangeFilterProps) => {
  const handlePreset = (value: DatePreset) => {
    if (value === 'custom') {
      onChange({ preset: 'custom', from, to });
    } else {
      // Presets (and "Any date") clear the custom range; effective bounds are computed at request time.
      onChange({ preset: value, from: null, to: null });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled}>
        <InputLabel id={`${idPrefix}-label`}>{label}</InputLabel>
        <Select
          labelId={`${idPrefix}-label`}
          value={preset}
          label={label}
          onChange={(e) => handlePreset(e.target.value as DatePreset)}
        >
          {DATE_PRESET_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'any'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {preset === 'custom' && (
        <>
          <DatePicker
            label="From"
            value={from}
            onChange={(v) => onChange({ preset: 'custom', from: v, to })}
            disabled={disabled}
            slotProps={{
              textField: { size: 'small', sx: { minWidth: 150 } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="To"
            value={to}
            onChange={(v) => onChange({ preset: 'custom', from, to: v })}
            disabled={disabled}
            slotProps={{
              textField: { size: 'small', sx: { minWidth: 150 } },
              field: { clearable: true },
            }}
          />
        </>
      )}
    </LocalizationProvider>
  );
};

export default DateRangeFilter;
